import { React, PT } from '@gasbuddy/react';
import moment from 'moment';
import BN from 'bignumber.js';
import {
  VictoryChart, VictoryAxis, VictoryBar, VictoryStack,
  VictoryTooltip, VictoryLabel, VictoryLine, VictoryLegend,
} from 'victory';
import { Grid, Statistic, Card, Dimmer, Loader, Segment, Table } from 'semantic-ui-react';

function safeNum(n) {
  if (n === '-') {
    return new BN(0);
  }
  if (typeof n === 'string') {
    return new BN(n.replace(/,/g, ''));
  }
  return new BN(n || 0);
}

function sum(arr, counterparty) {
  return (arr || [])
    .filter(s => s.counterparty === counterparty)
    .reduce((prev, curr) => prev.plus(curr.sum), new BN(0))
    .toFixed(2);
}

function fillDay(hours = []) {
  for (let i = 0; i < 24; i += 1) {
    if (!hours.find(h => Number(h.hour) === i)) {
      hours.push({ hour: i, sum: 0, created: 0, ready: 0, notready: 0 });
    }
  }
  hours.sort((a, b) => a.hour - b.hour);
}

function payid(id) {
  const m = id.match(/com\/transfers\/(.*)/);
  if (m) {
    return m[1];
  }
  return id;
}

function uniquePayments(batch) {
  if (batch.extra_data && batch.extra_data.payments) {
    const payIds = {};
    batch.extra_data.payments.forEach((p) => { payIds[payid(p)] = 1; });
    return Object.keys(payIds).length;
  }
  return 0;
}

function payments(batch) {
  if (batch.extra_data && batch.extra_data.payments) {
    const payAmounts = {};
    batch.extra_data.payments.forEach((p) => {
      const id = payid(p);
      payAmounts[id] = (batch.extra_data[p] || batch.extra_data[id]).amount;
    });
    return Object.keys(payAmounts).reduce(
      (p, c) => p.plus(payAmounts[c]),
      new BN(0),
    );
  }
  return new BN(0);
}

function unsettled(batch) {
  return safeNum(batch.batch_total || 0)
    .plus(batch.discount_total || 0)
    .plus(batch.credit_total || 0)
    .plus(batch.settled_total || 0)
    .plus(batch.non_gb_discount_total || 0)
    .minus(batch.non_gb_discount_deferred || 0);
}

function unsettledAds(batch) {
  return safeNum(batch.purchase_total || 0)
    .plus(batch.discount_total || 0)
    .plus(batch.gb_credit_total || 0)
    .plus(batch.settled_ads_credit_total || 0)
    .plus(batch.payment_total || 0);
}

export const Flashcards = ({ data, loading, date, batchOnly }) => {
  let wexUnsettled = '-';
  let wexWithBatch = '-';
  let wexAged = '-';
  let dwollaUnsettled = '-';
  let tx = 0;
  let tpv = '-';
  if (data && data.unsettled && Array.isArray(data.unsettled)) {
    wexAged = data.unsettled
      .filter(s => s.counterparty === 'wex')
      .reduce((prev, curr) => prev.plus(curr.aged), new BN(0))
      .toFixed(2);
    wexWithBatch = data.unsettled
      .filter(s => s.counterparty === 'wex')
      .reduce((prev, curr) => prev.plus(curr.with_batch), new BN(0))
      .toFixed(2);
    wexUnsettled = data.unsettled
      .filter(s => s.counterparty === 'wex')
      .reduce((prev, curr) => prev.plus(curr.sum), new BN(0))
      .minus(wexWithBatch)
      .minus(wexAged)
      .toFixed(2);
    dwollaUnsettled = new BN(sum(data.unsettled, 'dwolla')).neg().toFixed(2);
  }
  const dwollaBalance = data && data.dwolla && data.dwolla.balance && data.dwolla.balance.value;
  if (data && data.daily) {
    tpv = sum(data.daily, 'wex');
    tx = data.daily.filter(s => s.counterparty === 'wex')
      .reduce((prev, curr) => (prev + curr.count), 0);
  }
  const { enroll, batch, hourlyTx, yesterday, lastWeek, adsBatch } = data || {};
  [hourlyTx, yesterday, lastWeek].forEach((hr) => {
    if (!hr) {
      return;
    }
    fillDay(hr);
    hr.forEach((h) => {
      h.hour = Number(h.hour);
      h.sum = Number(safeNum(h.sum || 0).toFixed(2));
      if (hr === hourlyTx) {
        h.label = `${h.hour}h: $${h.sum}`;
      }
    });
  });

  // Destructuring default isn't enough because it might come back null
  const enrollByHour = enroll || [];
  fillDay(enrollByHour);
  enrollByHour.forEach((h) => {
    h.hour = Number(h.hour);
    h.created = Number(h.created);
    h.ready = Number(h.ready);
    h.notready = h.created - h.ready;
    h.label = `${h.hour}h: ${h.ready}/${h.created}`;
  });
  const created = enrollByHour.reduce((p, c) => p + Number(c.created), 0);
  const ready = enrollByHour.reduce((p, c) => p + Number(c.ready), 0);

  return (
    <Dimmer.Dimmable as={Segment} basic dimmed={loading}>
      <Dimmer active={loading}>
        <Loader />
      </Dimmer>
      <Grid padded columns="equal">
        <Grid.Row>
          {batch
            && (
              <Grid.Column>
                <Card fluid>
                  <Card.Content>
                    <Card.Header>
                      Wex Batch Report For
                      {' '}
                      {date.format('MMM DD, YYYY')}
                    </Card.Header>
                  </Card.Content>
                  <Card.Content>
                    <Table definition striped>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell />
                          <Table.HeaderCell>Count</Table.HeaderCell>
                          <Table.HeaderCell>Value</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>From Wex File:</Table.Cell>
                          <Table.Cell>{batch.batch_count}</Table.Cell>
                          <Table.Cell>
  $
                            {safeNum(batch.batch_total).toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>From GasBuddy Database:</Table.Cell>
                          <Table.Cell>{batch.billed_count}</Table.Cell>
                          <Table.Cell>
  $
                            {safeNum(batch.billed_total).toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>GasBuddy Discount:</Table.Cell>
                          <Table.Cell>{batch.discount_count}</Table.Cell>
                          <Table.Cell>
                            $
                            {safeNum(batch.discount_total).neg().toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Deferred Discounts:</Table.Cell>
                          <Table.Cell />
                          <Table.Cell>
                            $
                            {safeNum(batch.non_gb_discount_deferred).neg().toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Non GasBuddy Discounts:</Table.Cell>
                          <Table.Cell />
                          <Table.Cell>
                            $
                            {safeNum(batch.non_gb_discount_total).neg().toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Account Credits Applied:</Table.Cell>
                          <Table.Cell>{batch.credit_count}</Table.Cell>
                          <Table.Cell>
                            $
                            {safeNum(batch.credit_total).neg().toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Losses Taken:</Table.Cell>
                          <Table.Cell>{batch.loss_count}</Table.Cell>
                          <Table.Cell>
                            $
                            {safeNum(batch.loss_total).neg().toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Settled Funds From Customers:</Table.Cell>
                          <Table.Cell>{batch.settled_count}</Table.Cell>
                          <Table.Cell>
                            $
                            {safeNum(batch.settled_total).neg().toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row error={unsettled(batch).gt(0)}>
                          <Table.Cell>Unsettled/Unaccounted Funds:</Table.Cell>
                          <Table.Cell>
                            {(batch.batch_count || 0) - (batch.settled_count || 0)}
                          </Table.Cell>
                          <Table.Cell>
  $
                            {unsettled(batch).toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row error={!(safeNum(batch.batch_total).eq(payments(batch)))}>
                          <Table.Cell>Paid to Wex:</Table.Cell>
                          <Table.Cell>{uniquePayments(batch)}</Table.Cell>
                          <Table.Cell>
  $
                            {payments(batch).toFormat(2)}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </Card.Content>
                </Card>
              </Grid.Column>
            )
          }
          {adsBatch
            && (
              <Grid.Column>
                <Card fluid>
                  <Card.Content>
                    <Card.Header>
                      ADS Batch Report For
                      {' '}
                      {date.format('MMM DD, YYYY')}
                    </Card.Header>
                  </Card.Content>
                  <Card.Content>
                    <Table definition striped>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell />
                          <Table.HeaderCell>Count</Table.HeaderCell>
                          <Table.HeaderCell>Value</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>Total Purchase:</Table.Cell>
                          <Table.Cell>{adsBatch.purchase_count}</Table.Cell>
                          <Table.Cell>
                            {`$${safeNum(adsBatch.purchase_total).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>GasBuddy Discount:</Table.Cell>
                          <Table.Cell>{adsBatch.discount_count}</Table.Cell>
                          <Table.Cell>
                            {`$${safeNum(adsBatch.discount_total).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Deferred Discounts:</Table.Cell>
                          <Table.Cell />
                          <Table.Cell>
                            {`$${safeNum(adsBatch.non_gb_discount_deferred).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Non GasBuddy Discounts:</Table.Cell>
                          <Table.Cell />
                          <Table.Cell>
                            {`$${safeNum(adsBatch.non_gb_discount_total).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Account Credits Applied:</Table.Cell>
                          <Table.Cell>{adsBatch.gb_credit_count}</Table.Cell>
                          <Table.Cell>
                            {`$${safeNum(adsBatch.gb_credit_total).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Settled Ads Credits:</Table.Cell>
                          <Table.Cell>{adsBatch.settled_ads_credit_count}</Table.Cell>
                          <Table.Cell>
                            {`$${safeNum(adsBatch.settled_ads_credit_total).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row error={unsettledAds(adsBatch).isZero()}>
                          <Table.Cell>Unsettled/Unaccounted Credits:</Table.Cell>
                          <Table.Cell>
                            {(adsBatch.purchase_count || 0) - (adsBatch.settled_ads_credit_count || 0)}
                          </Table.Cell>
                          <Table.Cell>
                            {`$${unsettledAds(adsBatch).toFormat(2)}`}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </Card.Content>
                </Card>
              </Grid.Column>
            )
          }
        </Grid.Row>
        <Grid.Row>
          {!batchOnly && (
            <Grid.Column>
              <Card fluid>
                <Card.Content>
                  <Card.Header>
                    Transactions Occuring On
                    {' '}
                    {date.format('MMM DD, YYYY')}
                    {' '}
                    PST
                  </Card.Header>
                </Card.Content>
                <Card.Content>
                  <Card.Description style={{ textAlign: 'center' }}>
                    <div>
                      <Statistic>
                        <Statistic.Value>{tx}</Statistic.Value>
                        <Statistic.Label>Transactions</Statistic.Label>
                      </Statistic>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                      <Statistic>
                        <Statistic.Value>{`$${tpv}`}</Statistic.Value>
                        <Statistic.Label>TPV</Statistic.Label>
                      </Statistic>
                    </div>
                    {hourlyTx
                      && (
                      <VictoryChart animate>
                        <VictoryLegend
                          x={150}
                          y={270}
                          orientation="horizontal"
                          gutter={20}
                          data={[
                            { name: '-1 day', symbol: { fill: 'blue' } },
                            { name: '-7 days', symbol: { fill: 'green' } },
                          ]}
                        />
                        <VictoryAxis tickValues={[...Array(25).keys()]} tickFormat={x => ((x % 6) === 0 ? x : '')} />
                        <VictoryAxis
                          dependentAxis
                          label="$ (thousands)"
                          axisLabelComponent={<VictoryLabel dy={-12} />}
                          tickCount={2}
                          tickFormat={y => safeNum(y).dividedBy(1000).toFormat(1)}
                        />
                        <VictoryBar
                          labelComponent={<VictoryTooltip />}
                          data={hourlyTx}
                          x="hour"
                          y="sum"
                        />
                        {yesterday
                          && (
                          <VictoryLine
                            style={{ data: { stroke: 'blue', strokeWidth: 5 } }}
                            data={yesterday}
                            x="hour"
                            y="sum"
                          />
                          )
                        }
                        {lastWeek
                          && (
                          <VictoryLine
                            style={{ data: { stroke: 'green', strokeWidth: 5 } }}
                            data={lastWeek}
                            x="hour"
                            y="sum"
                          />
                          )
                        }
                      </VictoryChart>
                      )
                    }
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
          )}
          <Grid.Column>
            <Card fluid>
              <Card.Content>
                <Card.Header>Unsettled Activity</Card.Header>
              </Card.Content>
              <Card.Content>
                <Card.Description style={{ textAlign: 'center' }}>
                  {!batchOnly && (
                    <React.Fragment>
                      <div>
                        <Statistic>
                          <Statistic.Value>{`$${wexWithBatch}`}</Statistic.Value>
                          <Statistic.Label>WEX Billed Not Settled</Statistic.Label>
                        </Statistic>
                      </div>
                      <div style={{ marginTop: '20px' }}>
                        <Statistic>
                          <Statistic.Value>{`$${wexUnsettled}`}</Statistic.Value>
                          <Statistic.Label>WEX Unbilled &lt; 7 days</Statistic.Label>
                        </Statistic>
                      </div>
                      <div style={{ marginTop: '20px' }}>
                        <Statistic>
                          <Statistic.Value>{`$${wexAged}`}</Statistic.Value>
                          <Statistic.Label>WEX Unsettled &gt; 7 days</Statistic.Label>
                        </Statistic>
                      </div>
                      <div style={{ marginTop: '20px' }}>
                        <Statistic>
                          <Statistic.Value>{`$${dwollaUnsettled}`}</Statistic.Value>
                          <Statistic.Label>Pending Dwolla</Statistic.Label>
                        </Statistic>
                      </div>
                    </React.Fragment>
                  )}
                  <div style={{ marginTop: '20px' }}>
                    <Statistic>
                      <Statistic.Value>{`$${dwollaBalance}`}</Statistic.Value>
                      <Statistic.Label>Dwolla Balance</Statistic.Label>
                    </Statistic>
                  </div>
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
          <Grid.Column>
            <Card fluid style={{ height: '100%' }}>
              <Card.Content style={{ flex: 0 }}>
                <Card.Header>Enrollment (Daily PST)</Card.Header>
              </Card.Content>
              <Card.Content>
                <Card.Description style={{ textAlign: 'center' }}>
                  <div>
                    <Statistic>
                      <Statistic.Value>{created}</Statistic.Value>
                      <Statistic.Label>Completed Enrollments</Statistic.Label>
                    </Statistic>
                  </div>
                  <div style={{ marginTop: '20px' }}>
                    <Statistic>
                      <Statistic.Value>{ready || 0}</Statistic.Value>
                      <Statistic.Label>Ready to Ship</Statistic.Label>
                    </Statistic>
                  </div>
                  {enrollByHour
                    && (
                    <VictoryChart animate>
                      <VictoryAxis tickValues={[...Array(25).keys()]} tickFormat={x => ((x % 6) === 0 ? x : '')} />
                      <VictoryAxis
                        axisLabelComponent={<VictoryLabel dy={-12} />}
                        dependentAxis
                        label="Enrollments"
                        tickCount={3}
                      />
                      <VictoryStack>
                        <VictoryBar
                          labelComponent={<VictoryTooltip />}
                          data={enrollByHour}
                          x="hour"
                          y="ready"
                        />
                        <VictoryBar
                          style={{ data: { fill: 'red' } }}
                          labelComponent={<VictoryTooltip />}
                          data={enrollByHour}
                          x="hour"
                          y="notready"
                        />
                      </VictoryStack>
                    </VictoryChart>
                    )
                  }
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Dimmer.Dimmable>
  );
};

Flashcards.propTypes = {
  data: PT.shape({
    unsettled: PT.array,
    daily: PT.array,
    batch: PT.object,
  }),
  loading: PT.bool,
  date: PT.instanceOf(moment).isRequired,
  batchOnly: PT.bool,
};

Flashcards.defaultProps = {
  data: null,
  loading: false,
  batchOnly: false,
};
